import type { CSSProperties, ReactNode } from 'react';
import { ChevronLeft, ChevronRight, Scale, Lightbulb, ScrollText, Check, Lock, AlertTriangle, ArrowUpCircle } from 'lucide-react';
import { Book, Title, Rule, px, INK, INK_SOFT, SEAL, type BookEntry } from './SpellBook';
import { PX_FONT, TITLE_FONT } from './theme';
import { useAppState } from '../../hooks/useAppState';
import { usePrescription, TIERS, RANK, lossesFor } from '../ledger/PrescriptionEngine';
import { ruleGroups } from '../ledger/RulesSnapshot';
import type { Tier } from '../../lib/api';

/*
 * The Prescription Engine as a book: your prescription (setup on the left page, the three tiers on the
 * right), why (the record behind it and any step-up or step-down), and the firm's rules.
 * Same engine as the classic panel (usePrescription); only the page dress differs.
 */

/* Tier inks that read on parchment */
const INKED: Record<Tier, string> = { steady: '#3E6FA8', push: '#3E7A4E', max: '#B8641E' };
const usd = (n: number) => `$${Math.round(n).toLocaleString()}`;
const fmtR = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}R`;

const small: CSSProperties = { fontFamily: PX_FONT, fontSize: 9, letterSpacing: '0.08em', color: INK_SOFT, textTransform: 'uppercase' };
const chip = (on: boolean): CSSProperties => ({
  padding: '3px 7px', border: 'none', cursor: 'pointer', fontFamily: PX_FONT, fontSize: 10,
  color: on ? '#FBEBC8' : INK, background: on ? SEAL : 'rgba(255,248,226,0.55)', boxShadow: `0 0 0 2px ${INK}`,
});
const Row = ({ name, children }: { name: string; children: ReactNode }) => (
  <div style={{ alignSelf: 'stretch', display: 'flex', flexDirection: 'column', gap: 5, margin: `${px(2)}px ${px(2)}px 0` }}>
    <span style={small}>{name}</span>
    <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>{children}</div>
  </div>
);
const Note = ({ tone, icon, children }: { tone: string; icon: ReactNode; children: ReactNode }) => (
  <div style={{ alignSelf: 'stretch', margin: `${px(2)}px ${px(1)}px 0`, display: 'flex', gap: 8, padding: '5px 8px', background: 'rgba(255,248,226,0.5)', boxShadow: `inset 3px 0 0 ${tone}`, fontFamily: PX_FONT, fontSize: 9, lineHeight: 1.5, color: INK }}>
    <span style={{ color: tone, flex: 'none', marginTop: 1 }}>{icon}</span><span>{children}</span>
  </div>
);

export function PrescriptionBook({ replay, closed, onChosen }: { replay?: number; closed?: boolean; onChosen?: () => void }) {
  const { user } = useAppState();
  const username = user?.discordUsername || 'trader';
  const rx = usePrescription(username, onChosen);
  const { copy, firms, strategy, cycle, data, firm, entry, staleDays, plan, setPlanId, setFirmId, account, setSize, funded, setFunded,
    drawdown, rec, warn, chosen, upgrade, lock, saving, error, riskFor } = rx;

  // ── Your prescription: setup | the three tiers ──
  const setup = (
    <>
      <Title>Prescription</Title>
      <Rule />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button type="button" aria-label="Previous strategy" onClick={() => cycle(-1)} style={{ ...chip(false), padding: 3, display: 'grid' }}><ChevronLeft size={13} /></button>
        <span style={{ fontFamily: TITLE_FONT, fontSize: 24, color: SEAL, minWidth: 64, textAlign: 'center' }}>{strategy}</span>
        <button type="button" aria-label="Next strategy" onClick={() => cycle(1)} style={{ ...chip(false), padding: 3, display: 'grid' }}><ChevronRight size={13} /></button>
      </div>
      <Row name="Firm">
        <select aria-label="Prop firm" value={firm.id} onChange={e => { const f = firms.find(x => x.id === e.target.value)!; setFirmId(f.id); setPlanId(f.plans[0].id); }}
          style={{ ...chip(false), fontSize: 11, padding: '4px 6px' }}>
          {firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </Row>
      <Row name="Plan">{firm.plans.map(p => <button key={p.id} type="button" onClick={() => setPlanId(p.id)} style={chip(p.id === plan.id)}>{p.name}</button>)}</Row>
      <Row name="Account">{plan.accounts.map(a => <button key={a.size} type="button" onClick={() => setSize(a.size)} style={chip(a.size === account.size)}>${a.size / 1000}K</button>)}</Row>
      <Row name="Phase">{([['Eval', false], ['Funded', true]] as const).map(([n, v]) => <button key={n} type="button" onClick={() => setFunded(v)} style={chip(funded === v)}>{n}</button>)}</Row>
      <div style={{ marginTop: 'auto', textAlign: 'center' }}>
        <div style={small}>Max drawdown · {plan.drawdownType}</div>
        <div style={{ fontFamily: TITLE_FONT, fontSize: 26, color: INK }}>{usd(drawdown)}</div>
        {entry?.verified_on && (
          <div style={{ ...small, fontSize: 8, color: staleDays !== null && staleDays > 60 ? SEAL : INK_SOFT }}>
            Rules checked {new Date(entry.verified_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}{staleDays !== null && staleDays > 60 ? ' · may be stale' : ''}
          </div>
        )}
      </div>
    </>
  );

  // Counsel, under the sizes: the advised size, any step down or up, the playbook caveat
  const counsel = !data ? null : (
    <>
      {rec && <Note tone={INKED[rec.tier]} icon={<Lightbulb size={13} />}><b>Advised: {copy.tiers[rec.tier]}.</b> {rec.why.join(' · ')}.</Note>}
      {warn.length > 0 && chosen && chosen !== 'steady' && (
        <Note tone={SEAL} icon={<AlertTriangle size={13} />}><b>Consider dropping to {copy.tiers[TIERS[RANK[chosen] - 1]]}.</b> {warn.join(' · ')}.</Note>
      )}
      {upgrade && warn.length === 0 && <Note tone={INKED.push} icon={<ArrowUpCircle size={13} />}><b>You've earned {copy.tiers[rec!.tier]}.</b> {rec!.why.join(' · ')}.</Note>}
      {!data.inputs.ownData && (
        <Note tone={INKED.max} icon={<AlertTriangle size={13} />}>
          <b>Sized from the {strategy} playbook.</b> Until you've logged 20 trades the engine assumes a worst streak of {data.inputs.plannedStreak}; then it uses your own.
        </Note>
      )}
    </>
  );

  const tiers = !data ? <p style={{ ...small, margin: 'auto' }}>{error ?? 'Reading your trades…'}</p> : !data.ready ? (
    <div style={{ margin: 'auto', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: `0 ${px(4)}px` }}>
      <Lock size={22} color={SEAL} />
      <p style={{ fontFamily: PX_FONT, fontSize: 11, lineHeight: 1.6, color: INK, margin: 0 }}>Finish Readiness Progression for {strategy} (30 replay days and 30 paper trades, both positive) to write its prescription.</p>
    </div>
  ) : (
    <>
      <Title>Choose your size</Title>
      <div style={{ alignSelf: 'stretch', display: 'flex', flexDirection: 'column', gap: px(2), margin: `${px(2)}px ${px(1)}px 0` }}>
        {TIERS.map(t => {
          const s = data.inputs.worstStreak, losses = lossesFor(t, s), risk = riskFor(t), tone = INKED[t];
          const contracts = data.inputs.riskPerContract ? Math.floor(risk / data.inputs.riskPerContract) : null;
          const isChosen = chosen === t, isRec = rec?.tier === t;
          return (
            <div key={t} style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: '1px 8px', padding: '5px 8px',
              background: isChosen ? `${tone}22` : 'rgba(255,248,226,0.45)', boxShadow: `inset 3px 0 0 ${tone}${isChosen ? `, 0 0 0 2px ${tone}` : ''}` }}>
              <span style={{ fontFamily: TITLE_FONT, fontSize: 17, color: tone }}>{copy.tiers[t]}{isRec && <span style={{ ...small, fontSize: 7, marginLeft: 6, color: '#FBEBC8', background: tone, padding: '1px 4px', verticalAlign: 3 }}>Advised</span>}</span>
              <span style={{ fontFamily: TITLE_FONT, fontSize: 20, color: INK, textAlign: 'right' }}>{usd(risk)}</span>
              <span style={{ ...small, fontSize: 8, textTransform: 'none', letterSpacing: '0.03em' }}>
                {losses} losses to breach{contracts !== null ? ` · ${contracts} contract${contracts === 1 ? '' : 's'}` : ''}
              </span>
              <button type="button" onClick={() => lock(t, risk)} disabled={saving !== null}
                style={{ ...chip(!isChosen), padding: '2px 7px', fontSize: 9, display: 'flex', alignItems: 'center', gap: 4, background: isChosen ? 'transparent' : tone, color: isChosen ? tone : '#FBEBC8', boxShadow: `0 0 0 2px ${isChosen ? tone : INK}` }}>
                {isChosen ? <><Check size={10} /> Locked</> : saving === t ? 'Saving…' : 'Lock in'}
              </button>
              {/* One pip per loss this size survives: filled up to your worst streak */}
              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 2, flexWrap: 'wrap', marginTop: 3 }}>
                {Array.from({ length: Math.min(losses, 20) }, (_, k) => <span key={k} style={{ width: 6, height: 8, background: k < s ? tone : `${tone}44` }} />)}
              </div>
            </div>
          );
        })}
      </div>
      {counsel}
      {!chosen && <p style={{ ...small, color: SEAL, textAlign: 'center', marginTop: 'auto' }}>Lock in a size to open Live Trading for {strategy}</p>}
      {error && <p style={{ ...small, color: SEAL, textAlign: 'center' }}>{error}</p>}
    </>
  );

  // ── Why: the record behind it | step up or step down ──
  const stat = (name: string, value: string, sub?: string) => (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontFamily: TITLE_FONT, fontSize: 20, color: INK }}>{value}</div>
      <div style={{ ...small, fontSize: 8 }}>{name}</div>
      {sub && <div style={{ ...small, fontSize: 7, textTransform: 'none' }}>{sub}</div>}
    </div>
  );
  const record = !data ? null : (
    <>
      <Title>The record</Title>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: px(2), marginTop: px(3), alignSelf: 'stretch', margin: `0 ${px(2)}px` }}>
        {stat('Worst streak', String(data.inputs.worstStreak), data.inputs.ownData ? `last ${data.inputs.sample} trades` : `planned · ${data.inputs.sample} of 20 trades`)}
        {stat('Payouts', String(data.inputs.payouts))}
        {stat('Paper', String(data.inputs.paper.trades), fmtR(data.inputs.paper.expectancyR))}
        {stat('Live', String(data.inputs.live.trades), data.inputs.live.trades ? fmtR(data.inputs.live.expectancyR) : undefined)}
      </div>
    </>
  );
  // ── Firm rules, written out on the page: evaluation | funded stage and payouts ──
  const groups = ruleGroups(firm, plan, account);
  const ruleList = (g: { title: string; rows: [string, string][] }) => (
    <div key={g.title} style={{ alignSelf: 'stretch', margin: `${px(2)}px ${px(1)}px 0` }}>
      <div style={{ fontFamily: TITLE_FONT, fontSize: 15, color: SEAL, borderBottom: `2px solid ${INK}33`, paddingBottom: 3, marginBottom: 5 }}>{g.title}</div>
      {g.rows.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '2px 0', borderBottom: `1px dashed ${INK}2a` }}>
          <span style={{ ...small, fontSize: 8, textTransform: 'none', letterSpacing: '0.04em' }}>{k}</span>
          <span style={{ fontFamily: PX_FONT, fontSize: 10, color: INK, textAlign: 'right' }}>{v}</span>
        </div>
      ))}
    </div>
  );
  // One link per site (a firm's help pages are often many articles on the same site)
  const sources = [...new Map((entry?.source_urls ?? []).map(u => [new URL(u).hostname.replace(/^www\./, ''), u])).entries()];
  const rulesLeft = (
    <>
      {record}
      <div style={{ fontFamily: TITLE_FONT, fontSize: 17, color: INK, textAlign: 'center', marginTop: px(5) }}>{firm.name}</div>
      <div style={{ ...small, textAlign: 'center', textTransform: 'none' }}>{plan.name} · ${account.size / 1000}K</div>
      {groups.slice(0, 1).map(ruleList)}
    </>
  );
  const rulesRight = (
    <>
      {groups.slice(1).map(ruleList)}
      <p style={{ ...small, fontSize: 8, textTransform: 'none', lineHeight: 1.6, margin: `auto ${px(2)}px 0`, alignSelf: 'stretch' }}>
        {entry?.verified_on ? `Checked ${new Date(entry.verified_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}. ` : ''}Always confirm with the firm before you buy.
        {sources.length > 0 && <> Sources: {sources.map(([host, u], i) => <a key={host} href={u} target="_blank" rel="noreferrer" style={{ color: SEAL }}>{i ? ', ' : ''}{host}</a>)}</>}
      </p>
    </>
  );

  const entries: BookEntry[] = [
    { key: 'rx', name: 'Your prescription', icon: <Scale size={28} />, left: setup, right: tiers },
    { key: 'record', name: 'Record & firm rules', icon: <ScrollText size={28} />, left: rulesLeft, right: rulesRight },
  ];
  return <Book title="Prescription" entries={entries} replay={replay} closed={closed} />;
}
